"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { Heart } from "@/components/icons";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { handleError } from "@/lib/handle-error";
import { toast } from "sonner";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { lazyComponent } from "@/components/ui/lazy-component";

// WHY: loaded lazily so `@workos-inc/authkit-nextjs` stays out of the static graph of every listing card that renders this button.
const AuthDialog = lazyComponent(
  () =>
    import("@/components/auth/auth-dialog").then(
      (module) => module.AuthDialog,
    ),
  { loading: null, ssr: false },
);
import { ErrorBoundary } from "@/components/error/error-boundary";

export type SaveListingPresentation = "icon" | "overlay";

interface SaveListingButtonProps {
 productId: Id<"products">;
 listingTitle?: string;
 listingImageUrl?: string;
 initialSavedState?: boolean;
 syncWithServer?: boolean;
 presentation?: SaveListingPresentation;
}

export function SaveListingButton(props: SaveListingButtonProps) {
 return (
  <ErrorBoundary label="save-listing-button" fallback={null}>
   <SaveListingButtonInner {...props} />
  </ErrorBoundary>
 );
}

function SaveHeartButton({
 presentation,
 isSaved,
 isPending,
 onClick,
}: {
 presentation: SaveListingPresentation;
 isSaved: boolean;
 isPending: boolean;
 onClick: (event: React.MouseEvent) => void;
}) {
 return (
  <IconButton
   onClick={onClick}
   disabled={isPending}
   variant={presentation === "overlay" ? "outline" : "ghost"}
   size={presentation === "overlay" ? "default" : "sm"}
   className={cn(
    "text-foreground disabled:opacity-50",
    presentation === "overlay"
     ? "relative rounded-md max-md:size-8 max-md:before:absolute max-md:before:-inset-1.5 max-md:before:content-['']"
     : "rounded-lg border border-border hover:border-border hover:bg-muted max-md:size-11 max-md:border-0",
   )}
   aria-label={isSaved ? "Remove from saved" : "Save listing"}
  >
   <Heart
    className={cn(
     "size-4 transition-colors",
     presentation === "overlay" && "size-5 max-md:size-4",
     isSaved && "fill-destructive/100 text-destructive-strong",
    )}
   />
  </IconButton>
 );
}

function useSyncedSavedState(input: {
 productId: Id<"products">;
 isAuthenticated: boolean;
 isPending: boolean;
 initialSavedState: boolean;
 syncWithServer: boolean;
}) {
 const [isSaved, setIsSaved] = useState(input.initialSavedState);
 const savedQuery = useQuery(
  api.savedListings.queries.isSaved,
  input.isAuthenticated && input.syncWithServer
   ? { productId: input.productId }
   : "skip",
 );

 const serverSavedState = !input.isAuthenticated
  ? false
  : !input.isPending && typeof savedQuery === "boolean"
    ? savedQuery
    : null;
 const [appliedSavedState, setAppliedSavedState] = useState<boolean | null>(
  null,
 );

 if (serverSavedState !== null && serverSavedState !== appliedSavedState) {
  setAppliedSavedState(serverSavedState);
  setIsSaved(serverSavedState);
 }

 return { isSaved, setIsSaved };
}

function useSaveListing({
 productId,
 listingTitle,
 listingImageUrl,
 initialSavedState,
 syncWithServer = true,
}: Omit<SaveListingButtonProps, "presentation"> & {
 initialSavedState: boolean;
}) {
 const { isAuthenticated } = useConvexAuth();
 const [isPending, startTransition] = useTransition();
 const saveListing = useMutation(api.savedListings.mutations.save);
 const unsaveListing = useMutation(api.savedListings.mutations.unsave);
 const { isSaved, setIsSaved } = useSyncedSavedState({
  productId,
  isAuthenticated,
  isPending,
  initialSavedState,
  syncWithServer,
 });

 const runSave = useRunSave({
  productId,
  listingTitle,
  listingImageUrl,
  isSaved,
  setIsSaved,
  startTransition,
  saveListing,
  unsaveListing,
 });

 const gate = useSignInGate({
  isAuthenticated,
  onSignedIn: () => {
   if (!isSaved) runSave(true);
  },
 });

 const handleSave = (event: React.MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
  if (gate.deferUntilSignedIn()) return;
  runSave(!isSaved);
 };

 return { isSaved, isPending, handleSave, gate };
}

function useSignInGate({
 isAuthenticated,
 onSignedIn,
}: {
 isAuthenticated: boolean;
 onSignedIn: () => void;
}) {
 const [showAuthModal, setShowAuthModal] = useState(false);
 const [hasOpenedSignIn, setHasOpenedSignIn] = useState(false);
 const pendingSaveRef = useRef(false);

 useRunPendingSave(pendingSaveRef, isAuthenticated && !showAuthModal, async () =>
  onSignedIn(),
 );

 return {
  showAuthModal,
  setShowAuthModal,
  hasOpenedSignIn,
  pendingSaveRef,
  deferUntilSignedIn: () => {
   if (isAuthenticated) return false;
   pendingSaveRef.current = true;
   setHasOpenedSignIn(true);
   setShowAuthModal(true);
   return true;
  },
 };
}

function SaveListingButtonInner({
 presentation = "icon",
 listingTitle,
 listingImageUrl,
 initialSavedState = false,
 syncWithServer = true,
 productId,
}: SaveListingButtonProps) {
 const save = useSaveListing({
  productId,
  listingTitle,
  listingImageUrl,
  initialSavedState,
  syncWithServer,
 });

 return (
  <>
   <SaveHeartButton
    presentation={presentation}
    isSaved={save.isSaved}
    isPending={save.isPending}
    onClick={save.handleSave}
   />

   {save.gate.hasOpenedSignIn ? (
    <SaveSignInDialog
     open={save.gate.showAuthModal}
     onOpenChange={save.gate.setShowAuthModal}
     pendingSaveRef={save.gate.pendingSaveRef}
    />
   ) : null}
  </>
 );
}

function useRunPendingSave(
 pendingSaveRef: { current: boolean },
 ready: boolean,
 save: () => Promise<void>,
) {
 useEffect(() => {
  if (!ready || !pendingSaveRef.current) return;
  pendingSaveRef.current = false;
  void save();
 }, [ready, pendingSaveRef, save]);
}

function SaveSignInDialog({
 open,
 onOpenChange,
 pendingSaveRef,
}: {
 open: boolean;
 onOpenChange: (open: boolean) => void;
 pendingSaveRef: { current: boolean };
}) {
 const { isAuthenticated } = useConvexAuth();
 return (
  <AuthDialog
   open={open}
   onOpenChange={(next) => {
    onOpenChange(next);
    if (!next && !isAuthenticated) pendingSaveRef.current = false;
   }}
   entryPoint="listing_save"
   reason="Log in to save listings and compare them later."
   onSignedIn={() => onOpenChange(false)}
  />
 );
}

function useRunSave(input: {
 productId: Id<"products">;
 listingTitle?: string;
 listingImageUrl?: string;
 isSaved: boolean;
 setIsSaved: (value: boolean) => void;
 startTransition: (work: () => Promise<void>) => void;
 saveListing: ReturnType<typeof useMutation<typeof api.savedListings.mutations.save>>;
 unsaveListing: ReturnType<typeof useMutation<typeof api.savedListings.mutations.unsave>>;
}) {
 return (save: boolean) => {
  const previousState = input.isSaved;
  input.setIsSaved(save);
  input.startTransition(async () => {
   try {
    if (!save) {
     const result = await input.unsaveListing({ productId: input.productId });
     if (!result.success) throw new Error("Failed to remove listing");
     toast.success("Removed from saved listings");
    } else {
     await input.saveListing({
      productId: input.productId,
      listingTitle: input.listingTitle,
      listingImageUrl: input.listingImageUrl,
     });
     toast.success("Added to saved listings");
    }
   } catch (err) {
    input.setIsSaved(previousState);
    handleError(err);
   }
  });
 };
}
